/*
 *
 *  * Copyright(c) 2022 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import * as _ from 'underscore';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ModalConfig, ModalRef } from '@wfm/core';
import { TranslateService } from '@ngx-translate/core';
import { AuditService } from '@app/common/audit.service';
import { VirtualGridColumn } from '@wfm/virtual-grid';
import { appConstants } from '@app/common/app-constants';
import { Logger } from '@core/lib/services';

const log = new Logger('AuditTrailComponent');

@Component({
  selector: 'app-audit-trail',
  templateUrl: './audit-trail.component.html',
  styleUrls: ['./audit-trail.component.scss']
})
export class AuditTrailComponent implements OnInit, OnDestroy {
  patientsList: any[];
  loading = true;
  columns: Array<VirtualGridColumn>;
  auditItems: Array<any> = [];
  auditErrors: any;

  constructor(
    public config: ModalConfig,
    public modalRef: ModalRef,
    private translate: TranslateService,
    private cd: ChangeDetectorRef,
    private auditService: AuditService
  ) {}

  ngOnInit(): void {
    if (this.config.data.item) {
      const item = this.config.data.item;
      this.patientsList = [
        {
          label: item.patientName.text,
          data: [
            {
              [this.translate.instant('auditTrail.mrn')]: item.mrn.domainIdentifier,
              [this.translate.instant('auditTrail.accession')]: item.accession,
              [this.translate.instant('auditTrail.dob')]: item.patientDoB,
              [this.translate.instant('auditTrail.age')]: item.patientAge,
              [this.translate.instant('auditTrail.sex')]: item.patientGender,
              [this.translate.instant('patientHistory.modalityLabel')]: item.modality,
              [this.translate.instant('auditTrail.description')]: item.procedureText,
              [this.translate.instant('auditTrail.referringPhysician')]: item.referringPhysician.name.text
            }
          ]
        }
      ];

      this.auditService
        .getAuditInfo(item.examId)
        .then((data: any) => {
          if (data?.errors && data?.errors?.errorSource === 'UV Service' && data?.errors?.errorDescription) {
            this.auditService
              .getAuditInfo(item.examId)
              .then((data: any) => {
                this.setAuditInfo(data);
              })
              .catch((error: any) => {
                log.error('Exception occurred while getting audit info', error);
                this.loading = false;
              });
          } else {
            this.setAuditInfo(data);
          }
          this.loading = false;
        })
        .catch(error => {
          log.error('Exception occurred while getting audit info', error);
          this.loading = false;
        });
    }
  }

  private setAuditInfo(auditResult) {
    this.auditErrors = auditResult.errors;
    this.columns = [
      this.createAuditColumn('date-audit', 'eventDateTime', this.translate.instant('auditTrail.date')),
      this.createAuditColumn('time-audit', 'eventDateTime', this.translate.instant('auditTrail.time')),
      this.createAuditColumn('action-audit', 'eventActionType', this.translate.instant('auditTrail.action')),
      this.createAuditColumn('user1-audit', 'actor', this.translate.instant('auditTrail.user1')),
      this.createAuditColumn('user2-audit', '', this.translate.instant('auditTrail.user2'), item =>
        this.getOwnerName(item.participants, item.eventActionType)
      ),
      this.createAuditColumn(
        'device-destination-audit',
        'destination',
        this.translate.instant('auditTrail.device') + this.translate.instant('auditTrail.destination')
      ),
      this.createAuditColumn('comments-audit', 'comment', this.translate.instant('auditTrail.comments'))
    ];

    this.auditItems = [...auditResult.events];
    this.loading = false;
    this.cd.detectChanges();
  }

  private createAuditColumn(id: string, propName: string, headerLabel: string, vFormatter?: any): VirtualGridColumn {
    return {
      id: id,
      propertyIdentifier: id,
      propertyName: propName,
      headerLabel: this.translate.instant(headerLabel),
      isDraggable: false,
      isPinnable: false,
      isResizable: true,
      isHidden: false,
      isGroupable: false,
      defaultSort: undefined,
      filterConfig: undefined,
      valueFormatter: vFormatter
    } as VirtualGridColumn;
  }

  private getOwnerName(participants, actionType) {
    let owners;
    if (_.contains(appConstants.eventWithOwner, actionType)) {
      owners = _.pluck(
        _.filter(participants, participant => {
          return participant.type === 'user';
        }),
        'value'
      );
    }
    if (owners && owners.length > 0) {
      return owners[0];
    }
  }

  ngOnDestroy(): void {
    this.auditItems = undefined;
    this.columns = undefined;
    this.patientsList = undefined;
  }
}
